import React, { useState, useEffect, useContext } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
// material
import {
  Container,
  Stack,
  Card,
  Typography,
  CardHeader,
  Box,
  Button,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { red } from '@mui/material/colors';
// services
import { getOrder } from '../../services/Report';
import { getInvoice } from '../../services/Invoice';
// helper
import { getTimeZoneDate, getTimeZoneGTM, getEventDomain } from '../../helpers/Util';
// components
import Page from '../../components/Page';
import Order from '../../components/orders/order';
import TotalAmount from '../../components/orders/order/common/TotalAmount';
import PaymentStatus from '../../components/payment/Status';
import CartVoucher from '../../components/orders/order/common/CartVoucher';
// contexts
import { useAuth } from '../../contexts/Auth';
import { SettingsContext } from '../../contexts/Settings';

const Show = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();

  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);

  const [isLoading, setIsLoading] = useState(true);
  const [orderSale, setOrderSale] = useState({});
  const [hasError, setHasError] = useState(false);

  const downloadInvoice = async () => {
    try {
      const idToken = await authUser.getIdToken();
      const headers = {
        Authorization: `Bearer ${idToken}`,
        'Account-Key': authSettings.server.path
      };
      const invoice = await getInvoice({ headers, transactionId: transaction._id });

      if (!invoice) {
        alert('Invoice not found!');
        return;
      }

      const source = `data:application/pdf;base64,${invoice.document.output}`;
      const link = document.createElement('a');

      link.href = source;
      link.download = 'invoice.pdf';
      link.click();
    } catch (err) {
      console.error(err);
      alert('An error occurred!');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const eventId = searchParams.get('eventId');
        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };

        const orderSale = await getOrder({ headers, eventId, orderId });
        setOrderSale(orderSale);
        setIsLoading(false);
      } catch (err) {
        setHasError(true);
        console.error(err);
      }
    };

    fetchData();
  }, [orderId, authUser, authSettings, searchParams]);

  if (hasError) {
    return (
      <Container>
        <Alert severity="error">
          An error occurred while trying to load the orders — Please, try to refresh the page!
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  const path = `/order/${orderSale._id}`;

  const { transaction } = orderSale;
  const { order } = transaction;

  return (
    <Page title="Order" path={path}>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Order: #{order.number}
          </Typography>
        </Stack>

        <Box sx={{ mb: 3 }}>
          {!hasError ? (
            <Card
              sx={{
                ...(order.status === 'EXPIRED' && {
                  backgroundColor: red[50]
                })
              }}
            >
              <CardHeader
                title={
                  <>
                    <Link
                      href={`//${order.event.route?.subdomain}.${getEventDomain()}`}
                      target="_blank"
                      component={Link}
                    >
                      {order.event.name}
                    </Link>
                    {order.status === 'EXPIRED' && (
                      <Typography color="error" component="span">
                        {` ORDER CANCELLED`}
                      </Typography>
                    )}
                  </>
                }
                subheader={`${getTimeZoneDate({ date: order.event.date })
                  .tz('Europe/Lisbon')
                  .format('ddd, DD MMM')}
                  (GTM${getTimeZoneGTM({ date: order.event.date })})`}
                action={
                  <Button variant="outlined" endIcon={<DownloadIcon />} onClick={downloadInvoice}>
                    Invoice
                  </Button>
                }
              />
              <Box sx={{ m: 3 }} dir="ltr">
                <Order order={order} />
                <CartVoucher voucher={order.voucher} />
                <TotalAmount transaction={transaction} />
                <PaymentStatus transaction={transaction} />
              </Box>
            </Card>
          ) : (
            <Alert severity="error">
              An error occurred! Please, refresh the page to load the order data.
            </Alert>
          )}
        </Box>
      </Container>
    </Page>
  );
};

export default Show;
